import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  Box,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import CustomDatePicker from "../../components/Inputs/DatePickers/CustomDatePicker";
import { Column } from "../../Layout/utils";
import { ComparisonOptions } from "../../services/Alert";
import { formInputsStyles } from "../../Configuration/styles";
import { getPickupParams } from "./CreateFilterPickupForm";

/** Snapshots older than this are purged from
 *  `occupation_aggregats_master_photo_pickup_graph_histo`, so the sales-status
 *  chart has nothing to compare against beyond it. */
export const MAX_COMPARISON_DEPTH_IN_DAYS = 28;

export const DEFAULT_COMPARISON_DEPTH_IN_DAYS = 7;

const DEPTH_SHORTCUTS = [1, 3, 7, 14, 21, 28];

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    minWidth: "280px",
  },
  shortcuts: {
    width: "100%",
    marginTop: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "& button": {
      flex: "1 1 0px",
      borderColor: theme.vars.palette.text.primary,
    },
  },
}));

/** Comparison date for a given depth, i.e. "N days of pickup". */
export const comparisonDateFromDepth = (
  observationDate: DateTime,
  depthInDays: number
): string | null => observationDate.minus({ days: depthInDays }).toISODate();

export const CreateFilterComparisonForm = ({
  data,
  onSubmit,
}: {
  data: ComparisonOptions;
  onSubmit: (data: ComparisonOptions) => void;
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const inputStyles = formInputsStyles();

  const query = useQuery(["pickup"], getPickupParams);
  const observationDate = query.data?.date_observation;

  const [date, setDate] = useState<DateTime | null>(null);

  useEffect(() => {
    setDate(
      data?.comparison_date ? DateTime.fromISO(data.comparison_date) : null
    );
  }, [data]);

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    onSubmit({ comparison_date: date ? date.toISODate() : null });
  };

  if (!observationDate) return <FormattedMessage id="LOADING" />;

  const minDate = observationDate.minus({
    days: MAX_COMPARISON_DEPTH_IN_DAYS,
  });
  const maxDate = observationDate.minus({ days: 1 });
  const selectedDepth = date
    ? Math.round(observationDate.diff(date, "days").days)
    : null;

  return (
    <form
      id="create-filter-form"
      className={classes.root}
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Column sx={{ gap: 0 }}>
        <Typography
          variant="subtitle1"
          component="div"
          sx={{ fontWeight: "bold" }}
        >
          <FormattedMessage id="COMPARISON_DATE" />
        </Typography>
        <Typography variant="caption" component="div">
          <FormattedMessage
            id="COMPARISON_DATE_INFO"
            values={{ days: MAX_COMPARISON_DEPTH_IN_DAYS }}
          />
        </Typography>

        <Box
          className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
        >
          <ToggleButtonGroup
            value={selectedDepth}
            exclusive
            size="small"
            onChange={(_, depth: number | null) =>
              depth && setDate(observationDate.minus({ days: depth }))
            }
            className={classes.shortcuts}
          >
            {DEPTH_SHORTCUTS.map((depth) => (
              <ToggleButton key={depth} value={depth}>
                <Typography variant="subtitle1" component="span">
                  {`-${depth}`}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box
          className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
        >
          <CustomDatePicker
            label={intl.formatMessage({ id: "COMPARISON_DATE" })}
            translateLabel={false}
            minDate={minDate}
            maxDate={maxDate}
            value={date}
            onChange={(isoStr) =>
              setDate(isoStr ? DateTime.fromISO(isoStr) : null)
            }
          />
        </Box>
      </Column>
    </form>
  );
};

export default CreateFilterComparisonForm;
